import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import Section from "@/components/Section";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

function destForRole(role) {
  return role === "admin" ? "/admin" :
         role === "partner" ? "/partner" :
         role === "buyer" ? "/buyer" :
         "/client";
}

export function LoginPage() {
  const { login } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const user = await login(form.email, form.password);
      const next = params.get("next") || destForRole(user.role);
      nav(next, { replace: true });
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  const demos = [
    { email: "demo-admin@dt6.com", role: "Admin" },
    { email: "demo-client@dt6.com", role: "Client" },
    { email: "demo-partner@dt6.com", role: "Partner" },
    { email: "demo-buyer@dt6.com", role: "Buyer" },
  ];

  return (
    <>
      <PublicNav />
      <Section className="pt-16 pb-24 max-w-lg" testId="login-page">
        <div className="dt6-chip dt6-chip-primary mb-4">/ ACCESS</div>
        <h1 className="font-heading text-4xl font-black">Log in.</h1>
        <p className="mt-2 text-[color:var(--dt6-muted)]">Client, partner, and admin operators.</p>

        <form onSubmit={submit} className="dt6-card p-8 mt-8" data-testid="login-form">
          <div className="mb-4">
            <label className="dt6-label">Email</label>
            <input className="dt6-input" type="email" required value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} data-testid="login-email" autoComplete="email" />
          </div>
          <div className="mb-4">
            <label className="dt6-label">Password</label>
            <input className="dt6-input" type="password" required value={form.password} onChange={(e)=>setForm({...form,password:e.target.value})} data-testid="login-password" autoComplete="current-password" />
          </div>
          {err && <div className="mb-4 text-sm text-[color:var(--dt6-danger)]" data-testid="login-error">{err}</div>}
          <button type="submit" className="dt6-btn dt6-btn-primary w-full justify-center" disabled={busy} data-testid="login-submit">
            {busy ? "AUTHENTICATING..." : "Log in"}
          </button>
          <div className="mt-4 flex justify-between text-xs text-[color:var(--dt6-muted)]">
            <Link to="/forgot-password" className="dt6-link" data-testid="login-forgot">Forgot password?</Link>
            <Link to="/register" className="dt6-link" data-testid="login-register">Create account</Link>
          </div>
        </form>

        <div className="mt-6 dt6-card p-4">
          <div className="font-mono text-[0.7rem] tracking-[0.14em] uppercase text-[color:var(--dt6-muted)] mb-2">
            / DEMO ACCOUNTS (dev only)
          </div>
          <div className="space-y-1 text-xs">
            {demos.map((d) => (
              <div key={d.email} className="flex items-center justify-between">
                <button
                  type="button"
                  className="text-[color:var(--dt6-text)] hover:text-[color:var(--dt6-primary)]"
                  onClick={() => setForm({ email: d.email, password: "DT6demo!2026" })}
                  data-testid={`login-demo-${d.role.toLowerCase()}`}
                >
                  <span className="font-mono">{d.email}</span> <span className="text-[color:var(--dt6-muted)]">/ {d.role}</span>
                </button>
              </div>
            ))}
            <div className="pt-1 text-[color:var(--dt6-muted)]">password: <span className="font-mono">DT6demo!2026</span></div>
          </div>
        </div>
      </Section>
      <PublicFooter />
    </>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "client", org_name: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const user = await register(form);
      nav(destForRole(user.role), { replace: true });
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PublicNav />
      <Section className="pt-16 pb-24 max-w-lg" testId="register-page">
        <div className="dt6-chip dt6-chip-primary mb-4">/ SIGN UP</div>
        <h1 className="font-heading text-4xl font-black">Create your account.</h1>
        <form onSubmit={submit} className="dt6-card p-8 mt-8" data-testid="register-form">
          <div className="mb-4">
            <label className="dt6-label">Name</label>
            <input className="dt6-input" required value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid="register-name" />
          </div>
          <div className="mb-4">
            <label className="dt6-label">Email</label>
            <input className="dt6-input" type="email" required value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} data-testid="register-email" />
          </div>
          <div className="mb-4">
            <label className="dt6-label">Password (min 6 chars)</label>
            <input className="dt6-input" type="password" minLength={6} required value={form.password} onChange={(e)=>setForm({...form,password:e.target.value})} data-testid="register-password" />
          </div>
          <div className="mb-4">
            <label className="dt6-label">Organization</label>
            <input className="dt6-input" value={form.org_name} onChange={(e)=>setForm({...form,org_name:e.target.value})} data-testid="register-org" />
          </div>
          <div className="mb-4">
            <label className="dt6-label">Account type</label>
            <select className="dt6-select" value={form.role} onChange={(e)=>setForm({...form,role:e.target.value})} data-testid="register-role">
              <option value="client">Client</option>
              <option value="partner">Partner (consultant / agency)</option>
            </select>
          </div>
          {err && <div className="mb-4 text-sm text-[color:var(--dt6-danger)]" data-testid="register-error">{err}</div>}
          <button className="dt6-btn dt6-btn-primary w-full justify-center" disabled={busy} data-testid="register-submit">
            {busy ? "CREATING..." : "Create account"}
          </button>
          <div className="mt-4 text-xs text-[color:var(--dt6-muted)]">
            Already have an account? <Link to="/login" className="dt6-link">Log in</Link>
          </div>
        </form>
      </Section>
      <PublicFooter />
    </>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setMsg("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMsg(data.message || "If the account exists, a reset link was logged to the outbox.");
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <PublicNav />
      <Section className="pt-16 pb-24 max-w-lg" testId="forgot-page">
        <div className="dt6-chip dt6-chip-primary mb-4">/ RECOVERY</div>
        <h1 className="font-heading text-4xl font-black">Reset your password.</h1>
        <form onSubmit={submit} className="dt6-card p-8 mt-8" data-testid="forgot-form">
          <label className="dt6-label">Email</label>
          <input className="dt6-input" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} data-testid="forgot-email" />
          {msg && <div className="mt-4 text-sm text-[color:var(--dt6-success)]" data-testid="forgot-success">{msg}</div>}
          {err && <div className="mt-4 text-sm text-[color:var(--dt6-danger)]" data-testid="forgot-error">{err}</div>}
          <button className="dt6-btn dt6-btn-primary w-full justify-center mt-6" disabled={busy} data-testid="forgot-submit">
            {busy ? "SENDING..." : "Send reset link"}
          </button>
          <div className="mt-4 text-xs text-[color:var(--dt6-muted)]">
            <Link to="/login" className="dt6-link">Back to log in</Link>
          </div>
        </form>
        <div className="mt-4 text-xs text-[color:var(--dt6-muted)]">
          Note: email delivery is stubbed in this environment — reset links are logged to the admin outbox.
        </div>
      </Section>
      <PublicFooter />
    </>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setOk(true);
      setTimeout(() => nav("/login"), 1200);
    } catch (e2) {
      setErr(formatApiError(e2));
    }
  };
  return (
    <>
      <PublicNav />
      <Section className="pt-16 pb-24 max-w-lg" testId="reset-page">
        <div className="dt6-chip dt6-chip-primary mb-4">/ RESET</div>
        <h1 className="font-heading text-4xl font-black">Set new password.</h1>
        <form onSubmit={submit} className="dt6-card p-8 mt-8" data-testid="reset-form">
          <label className="dt6-label">Reset token</label>
          <input className="dt6-input" value={token} readOnly data-testid="reset-token" />
          <label className="dt6-label mt-4">New password</label>
          <input className="dt6-input" type="password" required minLength={6} value={pw} onChange={(e)=>setPw(e.target.value)} data-testid="reset-password" />
          {ok && <div className="mt-4 text-sm text-[color:var(--dt6-success)]" data-testid="reset-success">Password updated. Redirecting...</div>}
          {err && <div className="mt-4 text-sm text-[color:var(--dt6-danger)]" data-testid="reset-error">{err}</div>}
          <button className="dt6-btn dt6-btn-primary w-full justify-center mt-6" data-testid="reset-submit">Update password</button>
        </form>
      </Section>
      <PublicFooter />
    </>
  );
}
